/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import React, { useContext, useEffect, useState } from 'react';
import style from './PatientEducationDialog.scss?inline';
import { Close } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Button } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import { NodeType } from '../../../backendModels/report.model';
import dayjs from 'dayjs';
import { ReportsAPIContext } from '../../../provider/ReportsAPIProvider';
import { useCSS } from '../../../provider/CSSProvider';
import { hhmmToCorrectedDateJS } from '../../../utils/util';
import { MANUAL_EVENT_RECORD_ELEMENT_KEY, ManualEventRecord } from '../../../models/genericElements/events/manualEvent';
import { ConfirmationEvent } from '../../../models/manualEvents/confirmationEvent';
import { confirmationEventDetails } from '../../../config/patientEducationTexts';
import { RecordId } from '../../../backendModels/records.model';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapsUgcOutlinedIcon from '@mui/icons-material/MapsUgcOutlined';

interface PatientEducationDialogProps {
  open: boolean;
  onOpenChanged: (open: boolean) => void;
  label?: ConfirmationEvent;
}

function PatientEducationDialog({ open, onOpenChanged, label }: PatientEducationDialogProps) {
  useCSS(style);
  const { findMultipleRecords, manualEventEmit, deleteRecord } = useContext(ReportsAPIContext);

  const [details, setDetails] = useState<{ title: string; text: string }>({ title: '', text: '' });
  const [recordId, setRecordId] = useState<number | undefined>(undefined);
  const [timestamp, setTimestamp] = useState('');

  useEffect(() => {
    if (!label) {
      return;
    }
    setDetails(confirmationEventDetails[label]);

    if (open) {
      setTimestamp(dayjs().format('HH:mm'));
    }

    const eventRecords = findMultipleRecords(
      'generic',
      NodeType.GENERIC,
      MANUAL_EVENT_RECORD_ELEMENT_KEY
    ) as ManualEventRecord[];

    const curRecordId: RecordId | undefined = eventRecords.find(
      (record) => record.values?.eventName?.text === confirmationEventDetails[label].title
    )?.id;

    setRecordId(typeof curRecordId === 'number' ? curRecordId : undefined);
  }, [label, findMultipleRecords, open]);

  function closeDialog() {
    onOpenChanged(false);
  }

  function handleConfirmation() {
    if (timestamp == '' || details.title == '' || details.text == '') {
      return;
    }

    if (recordId) {
      deleteRecord(recordId, NodeType.GENERIC);
    } else {
      manualEventEmit(hhmmToCorrectedDateJS(timestamp).valueOf(), details.title);
    }
  }

  return (
    <Dialog className='patient-education-dialog' open={open} onClose={closeDialog}>
      <DialogTitle className='title'>
        {details.title}
        <IconButton className='close-icon-button' aria-label='close' onClick={closeDialog}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>{details.text}</Typography>
      </DialogContent>
      <DialogActions className='action-btn-container'>
        <Button
          variant={recordId ? 'contained' : 'text'}
          onClick={handleConfirmation}
          startIcon={recordId ? <CheckCircleIcon style={{ color: 'white' }} /> : <MapsUgcOutlinedIcon />}
          aria-label='als vorgelesen markieren'
          className='ready-button'>
          Vorgenommen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PatientEducationDialog;
